import { CANVAS } from '../tokens';
import { useCamera } from './camera';

/**
 * Two-finger pinch to zoom and pan.
 *
 * Touch devices have no wheel and no modifier keys, so pinch is the only way to
 * navigate. Tracked here rather than in the gesture machine because it is not a
 * gesture on the *content* — it moves the camera, and it must pre-empt whatever
 * single-finger gesture already began when the second finger landed.
 *
 * The transform is computed **absolutely**, from where the fingers are now
 * against where they started, never as a per-event delta. This matters: a
 * digitiser reports one `pointermove` per contact, so every other event sees a
 * half-updated pair. An incremental formulation reads that as a real change in
 * finger distance and drifts badly — a pure two-finger drag ends up zooming.
 */

type Tracked = { x: number; y: number };

const points = new Map<number, Tracked>();

type Anchor = {
  /** Finger separation when the gesture began. */
  startDistance: number;
  /** Camera scale when the gesture began. */
  startScale: number;
  /** World point under the initial midpoint — held under the fingers throughout. */
  worldX: number;
  worldY: number;
};

let anchor: Anchor | null = null;

/** True once two fingers are down, meaning single-touch gestures must yield. */
export const isPinching = (): boolean => anchor !== null;

const separation = (a: Tracked, b: Tracked) => Math.max(1, Math.hypot(a.x - b.x, a.y - b.y));

function pair(): [Tracked, Tracked] | null {
  if (points.size < 2) return null;
  const [a, b] = [...points.values()];
  return [a, b];
}

function begin(): void {
  const two = pair();
  if (!two) return;
  const [a, b] = two;
  const camera = useCamera.getState();
  const midX = (a.x + b.x) / 2;
  const midY = (a.y + b.y) / 2;
  const [worldX, worldY] = camera.toWorld(midX, midY);

  anchor = {
    startDistance: separation(a, b),
    startScale: camera.cam.s,
    worldX,
    worldY,
  };
}

export function pinchDown(event: React.PointerEvent): boolean {
  if (event.pointerType !== 'touch') return false;
  points.set(event.pointerId, { x: event.clientX, y: event.clientY });
  if (points.size === 2) {
    begin();
    return true;
  }
  return false;
}

export function pinchMove(event: React.PointerEvent): boolean {
  if (event.pointerType !== 'touch') return false;
  const tracked = points.get(event.pointerId);
  if (!tracked) return false;
  tracked.x = event.clientX;
  tracked.y = event.clientY;

  const two = pair();
  if (!two || !anchor) return false;

  const camera = useCamera.getState();
  const viewport = camera.viewport;
  if (!viewport) return true;

  const [a, b] = two;
  const scale = Math.min(
    CANVAS.maxZoom,
    Math.max(CANVAS.minZoom, anchor.startScale * (separation(a, b) / anchor.startDistance)),
  );

  // Place the camera so the anchored world point sits under the current
  // midpoint. Solving for the camera directly means the result depends only on
  // the fingers' present positions, so a partial update self-corrects.
  const rect = viewport.getBoundingClientRect();
  const midX = (a.x + b.x) / 2 - rect.left;
  const midY = (a.y + b.y) / 2 - rect.top;

  camera.set({ s: scale, x: midX - anchor.worldX * scale, y: midY - anchor.worldY * scale });
  return true;
}

export function pinchUp(event: React.PointerEvent): void {
  if (event.pointerType !== 'touch') return;
  points.delete(event.pointerId);
  // Keep suppressing content gestures until every finger is lifted, so the hand
  // that leaves last cannot start a drag on its way out.
  if (points.size < 2) anchor = null;
}

/** Forget all touch state, e.g. when the board unmounts. */
export function pinchReset(): void {
  points.clear();
  anchor = null;
}
